// Services
import breedService from "@/app/services/main/breed/breed-service.js";
import productsService from "@/app/services/main/product/product-service.js";

// Utils
import { vuelidateUtil } from "@/utils/vuelidate-util.js";

// Mixins
import { googleTagManagerMixins } from "@/app/mixins/utils/google-tag-manager-mixins";
import { toastMessageMixins } from "@/app/mixins/utils/toast-message-mixins";
import { dateMixins } from "@/app/mixins/utils/date-mixins";
import { tableMixins } from "@/app/mixins/utils/table-mixins.js";
import { comingSoonMixins } from "@/app/mixins/utils/coming-soon-mixins";

// Constants
import { STATUS_CODES } from "@/app/constants/status-constants";

// Third Party libraries
import DatePicker from "vue2-datepicker";
import { differenceInMonths } from "date-fns";

// Components
const ClientHeader = () =>
    import(
        /* webpackPrefetch: true, webpackChunkName: "client-header" */ "@/app/views/client-view/_shared/client-header/client-header.vue"
    );
const TheFooter = () =>
    import(
        /* webpackPrefetch: true, webpackChunkName: "the-footer" */ "@/app/views/main-view/_shared/the-footer/the-footer.vue"
    );

const ModalViewProductDetails = () =>
    import(
        /* webpackPrefetch: true, webpackChunkName: "modal-view-product-details" */ "@/app/views/client-view/public-products-page/components/modal-view-product-details/modal-view-product-details.vue"
    );

const PUPPY_AGE = {
    ADULT: "A",
    PUPPY: "P",
};

export default {
    name: "PublicProductsContainer",
    mixins: [googleTagManagerMixins, toastMessageMixins, dateMixins, tableMixins, comingSoonMixins],
    components: {
        ClientHeader,
        TheFooter,
        ModalViewProductDetails,
        DatePicker,
    },

    data() {
        return {
            showMainLogo: true,

            categories: [],
            preservedCategoriesData: [],
            productImages: [],
            index: null,

            isAPILoading: false,
            isFilterSearchLoading: true,

            breedList: [],

            displayedBreedName: "",
            inputtedPetName: "",
            displayedPetName: "",

            isFiltered: false,
            hasFilteredInsideCategory: false,
            isViewedMore: false,

            filters: { petBirth: "", selectedBreed: null },
            searchedBreed: null,
            searchedBirth: null,
            productsLimit: 8,
            selectedCategory: null,
            viewedProductsTotalCount: 0,
            isShowMore: false,
            isShowMoreLoading: false,

            publicCartItemsLength: this.$store.getters.getPublicCartItemsLength || 0,
        };
    },
    computed: {
        isNativeApp() {
            return this.$store.getters.getIsNativeApp;
        },
        validationRules() {
            const fields = {
                filters: {
                    selectedBreed: {},
                    petBirth: {},
                },
            };

            fields.filters = vuelidateUtil.setFieldsRequired(fields.filters, true);

            return fields;
        },
        displayShowMoreButton() {
            // fallback
            if (this.viewedProductsTotalCount <= this.categories[0].products.length) {
                return false;
            }

            return this.viewedProductsTotalCount != this.categories[0].products.length;
        },
        compPublicCartItemsLength() {
            return this.$store.getters.getPublicCartItemsLength;
        },
    },
    async created() {
        try {
            this.isAPILoading = true;
            await this.getBreedList();
            await this.getPublicProducts();

            await this.handleQueryParams();

            const { type: productType } = this.$route.query;

            // ! case: when the user is currently viewing a specific category and refreshes the page
            if (productType) {
                const category = this.categories.find((categ) => categ.name.toLowerCase() == productType.toLowerCase());

                this.isViewedMore = true;
                this.selectedCategory = { ...category, name: productType };
                await this.getProductsListByCategory({ limit: this.productsLimit });
            }
        } catch (err) {
            this.showGenericErrorToast();
        } finally {
            this.isAPILoading = false;
        }

        window.scrollTo(0, 0);
    },
    watch: {
        $route: {
            async handler(url) {
                const { type: productTypeId } = url.query;

                // case: when back button in the browser is used
                if (!productTypeId) {
                    this.categories = this.preservedCategoriesData;
                    this.isViewedMore = false;
                }
            },
        },
    },
    methods: {
        async handleQueryParams() {
            const { breedId, petBirth, petName } = this.$route.query;
            if (!breedId || !petBirth) {
                return;
            }

            const selectedBreed = this.breedList.find(breed => breed.id == breedId);

            this.filters.selectedBreed = selectedBreed;
            this.filters.petBirth = new Date(petBirth)
            this.inputtedPetName = petName || '';

            await this.searchProducts();

            this.$router.replace({
                path: this.$route.path,
                query: {}
            });
        },
        async getPublicProducts(filters = null) {
            const queryParams = {};

            if (filters) {
                const { breedId, age } = filters;

                if (breedId) {
                    queryParams["f_breedId"] = breedId;
                }

                if (age) {
                    queryParams["f_age"] = age;
                }
            }

            try {
                this.isFilterSearchLoading = true;
                const response = await productsService.getPublicProducts(queryParams);

                if (response.status === STATUS_CODES.SUCCESS) {
                    const categoriesData = response.data;

                    // only display categories with products
                    const filteredCategories = categoriesData.filter((categ) => categ.products.length > 0);
                    this.categories = filteredCategories || [];

                    // used when "<-" back arrow is clicked, if the user "view more" products
                    this.preservedCategoriesData = this.categories;
                } else {
                    this.showAPIErrorToast("Unable to fetch products list.", response);
                }
            } catch (error) {
                this.showGenericErrorToast();
            } finally {
                this.isFilterSearchLoading = false;
            }
        },
        async getBreedList() {
            try {
                const breedsResponse = await breedService.getBreeds();

                if (breedsResponse.status === STATUS_CODES.SUCCESS) {
                    const otherBreedOptions = breedsResponse.data.data.slice(0, 4);
                    const breeds = this.sortAlphabetically(breedsResponse.data.data.slice(4, breedsResponse.data.count));

                    // remove "Breed not specified", it has id = 0
                    this.breedList = [...otherBreedOptions, ...breeds].filter((breed) => breed.id != 0);
                } else {
                    const { ERROR_GET_BREED_LIST } = this.toastMessages.breed();
                    this.showAPIErrorToast(ERROR_GET_BREED_LIST, breedsResponse);
                }
            } catch (err) {
                this.showGenericErrorToast();
            }
        },
        async displayProductDetails(bundleId, categoryIndex, productIndex) {
            if (!bundleId) return;

            try {
                const response = await productsService.getPublicProductDetails(bundleId);

                if (response.status === STATUS_CODES.SUCCESS) {
                    const productDetails = response.data;

                    if (this.categories[categoryIndex]) {
                        if (this.categories[categoryIndex].products[productIndex]) {
                            const selectedProduct = this.categories[categoryIndex].products[productIndex];
                            const { imageUrl } = selectedProduct;

                            if (productDetails.isCustomBundle) {
                                const existingImages = productDetails.productImages || [];

                                const productImages = [{ imageUrl, primary: true }, ...existingImages];

                                // keep the breed based on the selected breed in the dropdown when viewing product/doggy pack details
                                const product = {
                                    ...productDetails,
                                    ...selectedProduct,
                                    breed: this.isFiltered ? this.displayedBreedName : selectedProduct.breed,
                                    productImages,
                                };

                                this.$refs.modalViewProductDetails.show({ ...product, petName: this.inputtedPetName });
                            } else {
                                this.$refs.modalViewProductDetails.show({
                                    ...productDetails,
                                    ...selectedProduct,
                                    petName: this.inputtedPetName,
                                    breed: this.isFiltered ? this.displayedBreedName : selectedProduct.breed,
                                });
                            }
                        }
                    } else {
                        this.$refs.modalViewProductDetails.show(productDetails);
                    }
                } else {
                    this.showAPIErrorToast("Unable to fetch product details.", response);
                }
            } catch (error) {
                this.showGenericErrorToast();
            }
        },
        async searchProducts() {
            const { selectedBreed, petBirth } = this.filters;

            if (!selectedBreed) {
                this.$v.filters.selectedBreed.$touch(); // will triger validation
            }

            if (!petBirth) {
                this.$v.filters.petBirth.$touch(); // will triger validation
            }

            if (!selectedBreed || !petBirth) {
                return;
            }

            this.searchedBreed = selectedBreed;
            this.searchedBirth = petBirth;

            this.$v.$reset(); // clear validation states for required fields (breed & date of birth)
            this.isFiltered = true;

            if (this.isViewedMore) {
                this.hasFilteredInsideCategory = true;
            }

            const puppyAge = this.calculatePuppyAge();
            const filters = {
                breedId: selectedBreed ? selectedBreed.id : "",
                age: puppyAge,
            };

            // case: when a category is "viewed more", the searching should be by the selected product category type and other filters (breed & DoB)
            if (this.selectedCategory) {
                // reset the products of the selected category since we are fetching new sets of products (to cater changes in the filtering on the same category)
                this.categories = this.categories.map((categ) => {
                    if (categ.id == this.selectedCategory.id) {
                        return { ...categ, products: [] };
                    }

                    return categ;
                });

                await this.getProductsListByCategory({ page: 1, limit: this.productsLimit });
                this.isViewedMore = true;
                this.page = 1;
            } else {
                await this.getPublicProducts(filters);
                this.isViewedMore = false; // to reset style changes
            }

            this.displayedBreedName = selectedBreed ? selectedBreed.name : "";

            // format inputted pet name to capitalize first letter of its first name
            if (this.inputtedPetName) {
                const capitalizedPetName =
                    this.inputtedPetName.charAt(0).toUpperCase() + this.inputtedPetName.slice(1).toLowerCase();
                this.displayedPetName = capitalizedPetName;
            } else {
                this.displayedPetName = "";
            }
        },
        async getTableContent(page = this.page, limit = this.limit, sortBy = this.sortBy, desc = this.desc) {
            const queryParams = { page, limit: this.productsLimit, sortBy, desc: true };
            const isShowMoreAction = true;
            await this.getProductsListByCategory(queryParams, isShowMoreAction);
        },
        async getProductsListByCategory(queryParams = {}, isShowMoreAction = false) {
            try {
                // to cater different loading states (and styles)
                if (!isShowMoreAction) {
                    this.isFilterSearchLoading = true;
                } else {
                    this.isShowMoreLoading = true;
                }

                const puppyAge = this.calculatePuppyAge();
                const isFilterValid = this.searchedBreed && puppyAge;

                // doggy pack has id of 0, which is falsy
                if (this.selectedCategory.id !== null || this.selectedCategory.id !== undefined) {
                    queryParams["f_productType"] = this.selectedCategory.id;
                }

                if (this.selectedCategory.isCustomBundle) {
                    queryParams["f_isCustomBundle"] = true;
                }

                if (this.searchedBreed && isFilterValid) {
                    queryParams["f_breedId"] = this.searchedBreed.id;
                }

                if (puppyAge && isFilterValid) {
                    queryParams["f_age"] = puppyAge;
                }

                const response = await productsService.getPublicCategoryProducts(queryParams);

                if (response.status === STATUS_CODES.SUCCESS) {
                    const { pagination, data: products } = response.data;

                    const selectedCategoryData = this.categories.find((category) => category.id == this.selectedCategory.id);

                    if (this.isShowMore) {
                        const currentProducts = this.categories[0].products;
                        this.categories = [{ ...selectedCategoryData, products: [...currentProducts, ...products] }];
                    } else {
                        this.categories = [{ ...selectedCategoryData, products }];
                    }

                    this.viewedProductsTotalCount = pagination.totalCount;
                } else {
                    this.showAPIErrorToast("Unable to fetch products list.", response);
                }
            } catch (error) {
                this.showGenericErrorToast();
            } finally {
                this.isFilterSearchLoading = false;
                this.isShowMoreLoading = false;
            }
        },
        async viewMoreProducts(selectedCategory) {
            if (!selectedCategory) return;

            this.selectedCategory = selectedCategory;

            await this.getProductsListByCategory({ limit: this.productsLimit });
            this.isViewedMore = true;

            this.$router.push({ path: "public-products", query: { type: selectedCategory.name } });

            this.filters = { petBirth: this.searchedBirth, selectedBreed: this.searchedBreed };
            this.inputtedPetName = this.displayedPetName;

            if (!this.searchedBreed || !this.searchedBirth) {
                this.$v.$reset(); // clear validation states for required fields (breed & date of birth)
            }
        },
        async resetViewedMore() {
            this.categories = this.preservedCategoriesData;

            this.isViewedMore = false;
            this.selectedCategory = null;
            this.page = 1;
            this.isShowMore = false;

            if (!this.searchedBreed || !this.searchedBirth) {
                this.filters = { petBirth: null, selectedBreed: null };
                this.searchedBreed = null;
                this.searchedBirth = null;
                this.displayedPetName = "";
                this.inputtedPetName = "";
                this.$v.$reset(); // clear validation states for required fields (breed & date of birth)
            } else {
                this.filters = { petBirth: this.searchedBirth, selectedBreed: this.searchedBreed };
                this.inputtedPetName = this.displayedPetName;
            }

            if (this.hasFilteredInsideCategory) {
                await this.searchProducts();
            }

            this.hasFilteredInsideCategory = false;

            let query = { ...this.$route.query };
            delete query.type; // Delete the type parameter
            this.$router.push({ path: "public-products", query }); // Update the route with the new query object, removing the 'type' in the url
        },
        showMore() {
            // pagination
            this.isShowMore = true;
            this.onPageSelected("next");
        },
        addToCart(event, productToAdd) {
            event.stopPropagation(); // this stops the details card from showing

            const cartItems = this.$store.getters.getPublicCartItems || [];
            const existingBundleIdIndex = cartItems.findIndex((product) => product.bundleId === productToAdd.bundleId);

            if (existingBundleIdIndex > -1) {
                this.showCustomErrorToast("Unable to add product.", "This product already exists in your cart.");
                return;
            }

            this.showCustomSuccessToast("Success!", "Product has been added to cart.");
            this.$store.dispatch("addToPublicCart", productToAdd);
        },
        calculatePuppyAge() {
            if (!this.searchedBreed || !this.searchedBirth) {
                return;
            }

            const startDate = new Date(this.searchedBirth);
            const endDate = new Date();

            const diffMonths = differenceInMonths(endDate, startDate);

            return diffMonths > parseInt(this.searchedBreed.adultAge) ? PUPPY_AGE.ADULT : PUPPY_AGE.PUPPY;
        },
        calculateLowestPrice(products) {
            if (!products || !products.length) {
                return;
            }

            const productsPrices = products.map(({ onSale, salePrice, price }) =>
                onSale ? parseFloat(salePrice) : parseFloat(price)
            );

            return Math.min(...productsPrices).toFixed(2);
        },
        replacePetName(description) {
            if (!description) {
                return description;
            }

            if (this.displayedPetName) {
                const capitalizedPetName =
                    this.displayedPetName.charAt(0).toUpperCase() + this.displayedPetName.slice(1).toLowerCase();

                return description.replace(new RegExp("PET_NAME", "gi"), capitalizedPetName);
            }

            return description.replace(new RegExp("PET_NAME", "gi"), "your dog");
        },
        getPrimaryImage(src) {
            return src ? src : require("@/assets/images/product/dog-default-picture-color.png");
        },
        replaceWithImagePlaceholder(event) {
            event.target.src = require("@/assets/images/product/dog-default-picture-color.png");
        },
        formatPrice(price) {
            return price ? parseFloat(price).toFixed(2) : price;
        },
        sortAlphabetically(items) {
            const sortedItems = items.sort(function (a, b) {
                return a.name.localeCompare(b.name);
            });
            return sortedItems;
        },
        onClicked(idx) {
            // gallery
            this.index = idx;
        },
        // Date Picker
        notAfterToday(date) {
            return date > new Date();
        },
        goToPublicCart() {
            if (this.displayedPetName) {
                this.$router.push({ path: '/public-cart', query: { petName: this.displayedPetName } });
            } else {
                this.$router.push("/public-cart");
            }
        },
    },

    validations() {
        return this.validationRules;
    },
};
